import { initApiClient } from '@/shared/api/apiClient';
import { getCitiesAroundSeo, getCityDetail, getNearbyCities } from '@/shared/api/generated/api';

/** Сколько первых карточек мозаики грузим с высоким приоритетом (кандидаты на LCP). */
export const MOSAIC_HIGH_PRIORITY_COUNT = 3;

/**
 * Сколько карточек мозаики видно без прокрутки: десктоп и планшет — две строки по 3, мобильный — около 4.
 * Все они грузятся сразу (не лениво), иначе браузер подхватит их позже и Speed Index вырастет.
 */
export const getFirstScreenMosaicCount = (): number => {
  if (typeof window === 'undefined') return MOSAIC_HIGH_PRIORITY_COUNT;

  return window.matchMedia('(max-width: 767px)').matches ? 4 : 6;
};

export const getCityTagsQuery = (slug: string) => ({
  city__slug: slug,
  page_size: 1000,
  sorting: '-popularity',
});

export type TNearbyResponse = Awaited<ReturnType<typeof getNearbyCities<false>>>;
export type TCityResponse = Awaited<ReturnType<typeof getCityDetail<false>>>;
export type TSeoResponse = Awaited<ReturnType<typeof getCitiesAroundSeo<false>>>;

/**
 * Темы экскурсий (`getCityTags`, page_size 1000) сюда намеренно не входят: это тяжёлый ответ,
 * нужный только SEO-блоку внизу страницы. Ранний запрос отнимал бы канал у картинок первого экрана (LCP);
 * страница запрашивает темы сама, когда SEO-блок приближается к вьюпорту.
 *
 * Промисы ранней загрузки никогда не отклоняются: при ошибке они возвращают `undefined`,
 * и страница сама повторяет запрос.
 */
export interface IAroundPrefetch {
  nearby: Promise<TNearbyResponse | undefined>;
  city: Promise<TCityResponse | undefined>;
  seo: Promise<TSeoResponse | undefined>;
}

const store = new Map<string, IAroundPrefetch>();
const preloadedImages = new Set<string>();

const settle = async <T>(request: Promise<T>): Promise<T | undefined> => {
  try {
    return await request;
  } catch {
    return undefined;
  }
};

// Браузер начинает качать картинку сразу после ответа API, не дожидаясь отрисовки Vue-компонентов.
// Тег <img> на странице потом переиспользует уже загружаемый ресурс (URL совпадает, crossorigin не задан).
const preloadImage = (url: string | undefined, isHighPriority: boolean) => {
  if (!url || preloadedImages.has(url) || typeof document === 'undefined') return;

  preloadedImages.add(url);

  const link = document.createElement('link');

  link.rel = 'preload';
  link.as = 'image';
  link.href = url;

  if (isHighPriority) {
    link.setAttribute('fetchpriority', 'high');
  }

  document.head.append(link);
};

const preloadFirstScreenImages = async (nearby: Promise<TNearbyResponse | undefined>) => {
  const response = await nearby;

  response?.data?.nearby_cities.slice(0, getFirstScreenMosaicCount()).forEach((city, index) => {
    preloadImage(city.image_url, index < MOSAIC_HIGH_PRIORITY_COUNT);
  });
};

/**
 * Запускает запросы страницы «Куда поехать» заранее — из хука роутера, до загрузки JS-чанков страницы.
 * Запросы идут параллельно со скачиванием кода, а страница потом забирает результат через `takeAroundPrefetch`.
 * Никогда не бросает исключений: при любой ошибке страница просто выполнит запросы сама.
 */
export const startAroundPrefetch = (slug: string) => {
  if (!slug || store.has(slug)) return;

  try {
    // Клиент API конфигурируется в App.ce.vue при монтировании, а хук роутера срабатывает раньше.
    initApiClient();

    const nearby = settle(getNearbyCities({ path: { slug } }));

    store.set(slug, {
      nearby,
      city: settle(getCityDetail({ path: { slug } })),
      seo: settle(getCitiesAroundSeo({ query: { ids: slug } })),
    });

    void preloadFirstScreenImages(nearby);
  } catch {
    store.delete(slug);
  }
};

/** Забирает результат ранней загрузки (однократно) или `null`, если её не было. */
export const takeAroundPrefetch = (slug: string): IAroundPrefetch | null => {
  const prefetch = store.get(slug) ?? null;

  store.delete(slug);

  return prefetch;
};
